import { useState } from 'react';
import { BottomSheet, ContentDivider } from '@kcd/design-system';

import { useCurrentUser } from '~/domains/auth/queries';
import CouponBadges from '~/domains/coupons/components/CouponBadges';
import CouponCard from '~/domains/coupons/components/CouponCard';
import { useCouponDetail } from '~/domains/coupons/queries';
import { genCouponHelpText, genCouponTitle } from '~/domains/coupons/utils';
import type { OAISchema } from '~/types/openapi';

import { ChevronRightIcon, SystemCheckOutlineIcon } from '~/assets/icons';

type Props = {
  data: OAISchema['MarketProductDetailsResponseV2'];
};

export function DiscountBenefitCard({ data }: Props) {
  const {
    discountResult: { maximumDiscountAmount, items },
  } = data;
  const { user } = useCurrentUser();
  const { data: couponDetaiData } = useCouponDetail(data.maxDiscountCouponId);
  const [opened, setOpened] = useState(false);

  const couponDetail = couponDetaiData?.[0];

  return (
    <>
      <div className="flex flex-col gap-12 rounded-16 border border-solid  border-03 px-16 py-12">
        <div className="flex items-start justify-between gap-16">
          <p className="text-primary-regular typo-paragraph300-semibold">
            {user?.name} 님의 최대 할인 금액
          </p>
          <p className="min-w-80 text-right text-paragraph typo-paragraph300-semibold">
            {maximumDiscountAmount.toLocaleString()}원
          </p>
        </div>
        <ContentDivider />
        <div className="flex flex-col gap-4 text-description">
          {items.map((result) => {
            return (
              <div
                className="flex justify-between gap-16"
                key={result.discountType}
              >
                <p className="flex-1 truncate typo-paragraph100-regular">
                  {result.description}
                </p>
                <p className="typo-paragraph100-semibold">
                  {result.discountAmount.toLocaleString()}원
                </p>
              </div>
            );
          })}
        </div>
        <ContentDivider />
        <button
          type="button"
          className="flex w-full items-center justify-center gap-4 text-description typo-caption100-regular"
          onClick={() => setOpened(true)}
        >
          <p>쿠폰 자세히 보기</p>
          <ChevronRightIcon className="size-20" />
        </button>
      </div>
      <BottomSheet
        className="z-20"
        opened={opened}
        onClose={() => setOpened(false)}
        title="최대 적용 가능 쿠폰"
      >
        {couponDetail ? (
          <CouponCard
            title={genCouponTitle(couponDetail)}
            description={couponDetail.name!}
            helpText={genCouponHelpText(couponDetail)}
            badgeSection={<CouponBadges coupon={couponDetail} />}
            notice={couponDetail.notice || []}
            suffix={
              <div className="flex min-w-48 flex-col items-center justify-center">
                <SystemCheckOutlineIcon className="size-20 text-icon" />
                <p className="text-icon typo-footnote300-semibold">적용가능</p>
              </div>
            }
          />
        ) : (
          <div className="mb-20 flex h-[134px] w-full items-center justify-center rounded-16 border border-solid border-02">
            <p className="text-paragraph typo-paragraph100-regular">
              적용 가능한 쿠폰이 없습니다.
            </p>
          </div>
        )}
      </BottomSheet>
    </>
  );
}
